import React, {ReactElement, useEffect, useMemo, useState} from 'react';

import {gt} from 'app/utils/i18n';

import {PaginatedPage} from './PaginatedPage';
import StateButton from './StateButton';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ListResponseType} from 'sweet-types';

export interface PaginatedListProps<P, D> {
  hook: UseListQueryType<P, D>;
  params: P;
  render: (data: undefined | ListResponseType<D>) => ReactElement | null;
  onIsLoading?: (isLoading: boolean) => unknown;
}

export const PaginatedList = <P, D>({
  hook,
  params,
  render,
  onIsLoading,
}: PaginatedListProps<P, D>) => {
  const [pages, setPages] = useState<Array<number>>([1]);
  const [hasNext, setHasNext] = useState<undefined | null | string>(null);
  const [loading, setLoading] = useState<Record<number, boolean>>({});

  const loadMore = () => {
    setPages((prevPages) => [...prevPages, prevPages.length + 1]);
  };

  const isLoading = useMemo(() => {
    return Object.values(loading).some((value) => value === true);
  }, [loading]);

  useEffect(() => {
    onIsLoading && onIsLoading(isLoading);
  }, [isLoading]);

  const removePage = (page: number) => {
    const pageIndex = pages.indexOf(page);

    if (pageIndex != -1) {
      setPages(pages.slice(0, pageIndex));
      setLoading((loading) => {
        delete loading[page];
        return loading;
      });
    }
  };

  return (
    <div>
      {pages.map((page, index) => (
        <PaginatedPage
          key={index}
          page={page}
          params={params}
          hook={hook}
          render={render}
          setHasNext={setHasNext}
          setIsLoading={(page, isLoading) => {
            setLoading((loading) => ({...loading, page: isLoading}));
          }}
          removePage={removePage}
        />
      ))}
      {hasNext != null && (
        <div className="d-block px-4 mt-3">
          <StateButton
            className="mb-2 btn-block btn-outline-primary btn-sm"
            isIdle={!isLoading}
            busyText={gt('Loading...')}
            onClick={loadMore}
          >
            {gt('Load more')}
          </StateButton>
        </div>
      )}
    </div>
  );
};
